import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Download, ChevronDown } from "lucide-react";
import { DetailHeader } from "@/components/layout/detail-header";
import { api } from "@/lib/api";
import { useToast } from "@/contexts/toast-context";

interface Test {
  id: string;
  name: string;
  description?: string;
  group?: string;
  isAutomatable: boolean;
  createdAt: string;
  credentials?: {
    apiKey?: string;
  };
}

export function TestDetailsPage() {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  
  const [test, setTest] = useState<Test | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    group: "",
    isAutomatable: false,
    apiKey: ""
  });
  const [availableGroups, setAvailableGroups] = useState<string[]>([]);
  const [isChanged, setIsChanged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showGenerateMenu, setShowGenerateMenu] = useState(false);

  // Load test data
  useEffect(() => {
    if (testId) {
      loadTest();
    }
  }, [testId]);

  // Load available groups
  useEffect(() => {
    loadGroups();
  }, []);

  // Update form data when test changes
  useEffect(() => {
    if (test) {
      setFormData({
        name: test.name,
        description: test.description || "",
        group: test.group || "",
        isAutomatable: test.isAutomatable,
        apiKey: test.credentials?.apiKey || ""
      });
    }
  }, [test]);

  const loadTest = async () => {
    if (!testId) return;
    
    try {
      setIsLoading(true);
      const testData = await api.get<Test>(`/test/${testId}`);
      setTest(testData);
    } catch (error) {
      console.error("Failed to load test:", error);
      showError("Failed to load test", "Could not load test details. Please try again.");
      navigate("/app/tests");
    } finally {
      setIsLoading(false);
    }
  };

  // Check for changes
  useEffect(() => {
    if (!test) return;
    
    const hasChanges = 
      formData.name !== test.name ||
      formData.description !== (test.description || "") ||
      formData.group !== (test.group || "") ||
      formData.isAutomatable !== test.isAutomatable ||
      formData.apiKey !== (test.credentials?.apiKey || "");
    
    setIsChanged(hasChanges);
  }, [formData, test]);

  const loadGroups = async () => {
    try {
      const tests = await api.get<Test[]>("/test");
      const groups = new Set<string>();
      tests.forEach(t => {
        if (t.group?.trim()) {
          groups.add(t.group.trim());
        }
      });
      setAvailableGroups(Array.from(groups).sort());
    } catch (error) {
      console.error("Failed to load groups:", error);
    }
  };

  const getGroupOptions = () => {
    return availableGroups.map(group => ({
      value: group,
      label: group
    }));
  };

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!test || isSaving) return;
    
    try {
      setIsSaving(true);
      
      // Capture current form data at the moment of save
      // This prevents race conditions from affecting the save operation
      const capturedFormData = {
        name: formData.name,
        description: formData.description,
        group: formData.group,
        isAutomatable: formData.isAutomatable,
        apiKey: formData.apiKey
      };
      
      // Determine API key value to send
      let apiKeyValue = "";
      
      if (capturedFormData.apiKey.trim() && !capturedFormData.apiKey.includes('*')) {
        // User entered a new API key
        apiKeyValue = capturedFormData.apiKey.trim();
      } else if (capturedFormData.apiKey.includes('*') || (test.credentials?.apiKey && capturedFormData.apiKey === "")) {
        // Field contains masked value OR there's an existing key but field is empty (preserve existing)
        apiKeyValue = "sk_****************************************";
      } else {
        // User explicitly cleared the field or no existing key
        apiKeyValue = "";
      }

      const updateData: any = {
        name: capturedFormData.name.trim(),
        description: capturedFormData.description.trim() || "",
        group: capturedFormData.group.trim() || null,
        isAutomatable: capturedFormData.isAutomatable,
        credentials: {
          apiKey: apiKeyValue
        }
      };
      
      const updatedTest = await api.patch<Test>(`/test/${test.id}`, updateData);
      
      // Update the local test state
      setTest(updatedTest);
      
      showSuccess("Test Updated", "Test details have been saved successfully.");
      
    } catch (error) {
      console.error("Failed to save test:", error);
      showError("Save Failed", "Could not save test details. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!test) return;
    
    try {
      // Get the latest test data
      const testResponse = await api.get(`/test/${test.id}/download`);
      
      // Get the expectations
      const expectationsResponse = await api.get(`/test/${test.id}/expectations`);
      
      // Get the test runs
      const runsResponse = await api.get(`/test/${test.id}/runs`);
      
      // Get the test steps
      const stepsResponse = await api.get(`/test/${test.id}/steps`);
      
      // Combine all data
      const testData = {
        test: testResponse,
        expectations: expectationsResponse,
        runs: runsResponse,
        steps: stepsResponse,
        exported: {
          timestamp: new Date().toISOString(),
          version: "1.0"
        }
      };
      
      // Create a Blob with the JSON data
      const blob = new Blob([JSON.stringify(testData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `test-${test.name.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      showSuccess("Download Complete", "Test data downloaded successfully");
    } catch (error) {
      console.error("Failed to download test data:", error);
      showError("Download Failed", "Could not download test data. Please try again.");
    }
  };

  const handleGenerateReport = async () => {
    if (!test) return;
    
    try {
      // Get auth token for the URL
      const token = localStorage.getItem('auth_token');
      
      // Set up query parameters for the report
      const queryParams = new URLSearchParams({
        timeRange: 'ALL',
        runStatus: 'ALL', 
        detailedTimingView: 'true',
        detailedExpectationView: 'true',
        detailedRunView: 'true',
        token: token || ''
      });

      setShowGenerateMenu(false);
      
      // Open report in new window
      window.open(`/test/${test.id}/report?${queryParams}`, '_blank');
      showSuccess("Report Generated", "Report generated successfully");
    } catch (error) {
      console.error("Failed to generate report:", error);
      showError("Generate Failed", "Could not generate report. Please try again.");
    }
  };

  const handleGenerateSheet = async () => {
    if (!test) return;
    
    try {
      // Set up query parameters for the sheet
      const queryParams = new URLSearchParams({
        timeRange: 'ALL',
        runStatus: 'ALL'
      });

      setShowGenerateMenu(false);
      
      // Fetch the Excel file
      const response = await fetch(`/api/test/${test.id}/sheet?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate sheet');
      }
      
      // Get the Excel blob from the response
      const xlsxBlob = await response.blob();
      const url = window.URL.createObjectURL(xlsxBlob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `test-sheet-${test.name.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      showSuccess("Sheet Generated", "Sheet generated and downloaded successfully");
    } catch (error) {
      console.error("Failed to generate sheet:", error);
      showError("Generate Failed", "Could not generate sheet. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-sm text-muted-foreground">Loading test details...</div>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-sm text-muted-foreground">Test not found</div>
        </div>
      </div>
    );
  }

  const breadcrumb = [
    { label: "All Tests", href: "/app/tests" },
    { label: test.name }
  ];

  const toggleOptions = [
    { id: "details", label: "Details" },
    { id: "explorer", label: "Explorer" },
    { id: "files", label: "Files" },
    { id: "flow", label: "Flow" },
    { id: "expectations", label: "Expectations" },
    { id: "runs", label: "Runs" },
  ];

  const handleToggleChange = (tabId: string) => {
    if (tabId === "details") {
      navigate(`/app/tests/${testId}`);
    } else {
      navigate(`/app/tests/${testId}/${tabId}`);
    }
  };

  const headerActions = (
    <div className="flex items-center gap-2">
      <Button
        variant="secondary"
        size="sm"
        onClick={handleDownload}
        className="text-xs"
      >
        <Download className="w-4 h-4 mr-1" />
        Download
      </Button>
      
      <div className="relative">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowGenerateMenu(!showGenerateMenu)}
          className="text-xs"
        >
          Generate
          <ChevronDown className="w-4 h-4 ml-1" />
        </Button>
        
        {showGenerateMenu && (
          <div className="absolute top-full right-0 z-50 mt-1 bg-background border border-border rounded-md shadow-lg min-w-[120px]">
            <button
              onClick={handleGenerateReport}
              className="w-full px-3 py-2 text-xs text-left hover:bg-muted transition-colors"
            >
              Report
            </button>
            <button
              onClick={handleGenerateSheet}
              className="w-full px-3 py-2 text-xs text-left hover:bg-muted transition-colors"
            >
              Sheet
            </button>
          </div>
        )}
      </div>

      <Button
        onClick={handleSave}
        disabled={!isChanged || isSaving}
        size="sm"
        className="text-xs"
      >
        {isSaving ? "Saving..." : "Save Changes"}
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={breadcrumb}
        toggleOptions={toggleOptions}
        activeToggle="details"
        onToggleChange={handleToggleChange}
        actions={headerActions}
      />

      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-none">
          {/* Basic Information */}
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold mb-4">Basic Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="Test name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                    placeholder="Describe what this test does..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* API Credentials */}
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold mb-4">API Credentials</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    API Key
                  </label>
                  <div className="mb-2">
                    <span className="text-xs text-muted-foreground">
                      {test.credentials?.apiKey ? "Current key is masked for security. Enter a new key to update." : "Enter an API key for this test."}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={formData.apiKey}
                    onChange={(e) => handleInputChange("apiKey", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder={test.credentials?.apiKey ? "Enter new API Key to update" : "Enter API Key"}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Group</label>
                  <Combobox
                    value={formData.group}
                    onChange={(value) => handleInputChange("group", value)}
                    options={getGroupOptions()}
                    placeholder="Enter or select group"
                    allowFreeText={true}
                    className="w-full"
                  />
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="isAutomatable"
                    checked={formData.isAutomatable}
                    onChange={(e) => handleInputChange("isAutomatable", e.target.checked)}
                  />
                  <label htmlFor="isAutomatable" className="text-sm font-medium">
                    Can be automated?
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}